import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

export const PRACTICE_AREAS = ["Corporate/M&A", "Litigation & Dispute Resolution", "Banking & Finance", "Capital Markets", "Real Estate", "Tax", "IP",
  "Employment", "Competition", "Arbitration", "Regulatory/Compliance", "General Counsel/In-house"];
const num = (v) => (v === "" ? null : Number(v));
const SELECT = "block h-9 w-full rounded-lg border border-line bg-white px-2 text-sm";

export const pqeText = (j) => {
  if (j.pqe_min != null && j.pqe_max != null) return `${j.pqe_min}–${j.pqe_max} years PQE`;
  if (j.pqe_min != null) return `${j.pqe_min}+ years PQE`;
  if (j.pqe_max != null) return `Up to ${j.pqe_max} years PQE`;
  return null;
};

export const jobMeta = (j) => [j.practice_area, j.location, j.law_firm, pqeText(j)].filter(Boolean);

function PracticeArea({ value, onChange, id, testId }) {
  const [other, setOther] = useState(!!value && !PRACTICE_AREAS.includes(value));
  const pick = (v) => { if (v === "__other") { setOther(true); onChange(""); } else { setOther(false); onChange(v); } };
  return (
    <div className="space-y-2">
      <select id={id} value={other ? "__other" : value || ""} onChange={(e) => pick(e.target.value)} className={SELECT} data-testid={testId}>
        <option value="">Choose a practice area…</option>
        {PRACTICE_AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
        <option value="__other">Other (type it)</option>
      </select>
      {other && <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="e.g. Insolvency & Restructuring" maxLength={80} aria-label="Other practice area" data-testid={`${testId}-other`} />}
    </div>
  );
}

function PqeRange({ form, set, prefix }) {
  const bad = form.pqe_min != null && form.pqe_max != null && form.pqe_min > form.pqe_max;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input id={`${prefix}-pqe-min`} type="number" min={0} max={40} step={0.5} placeholder="Min" aria-label="PQE minimum (years)" value={form.pqe_min ?? ""} onChange={(e) => set("pqe_min", num(e.target.value))} data-testid={`${prefix}-pqe-min-input`} />
        <span className="text-sm text-ink-muted">to</span>
        <Input id={`${prefix}-pqe-max`} type="number" min={0} max={40} step={0.5} placeholder="Max (optional)" aria-label="PQE maximum (years)" value={form.pqe_max ?? ""} onChange={(e) => set("pqe_max", num(e.target.value))} data-testid={`${prefix}-pqe-max-input`} />
        <span className="whitespace-nowrap text-sm text-ink-muted">years</span>
      </div>
      <p className="text-xs text-ink-muted">Post-qualification experience. Leave the maximum empty for "3+ years".</p>
      {bad && <p role="alert" className="text-xs text-risk" data-testid={`${prefix}-pqe-error`}>PQE minimum can't be more than the maximum.</p>}
    </div>
  );
}

export function PrimaryJobFields({ form, setForm, prefix = "job" }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid={`${prefix}-primary-fields`}>
      <div className="space-y-2 md:col-span-2">
        <Label htmlFor={`${prefix}-title`}>Job title</Label>
        <Input id={`${prefix}-title`} value={form.title || ""} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Senior Associate – Corporate M&A" data-testid={`${prefix}-title-input`} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}-practice-area`}>Practice area</Label>
        <PracticeArea id={`${prefix}-practice-area`} value={form.practice_area} onChange={(v) => set("practice_area", v)} testId={`${prefix}-practice-area-input`} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}-location`}>Location</Label>
        <Input id={`${prefix}-location`} value={form.location || ""} onChange={(e) => set("location", e.target.value)} placeholder="e.g. Mumbai" data-testid={`${prefix}-location-input`} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}-law-firm`}>Law firm</Label>
        <Input id={`${prefix}-law-firm`} value={form.law_firm || ""} onChange={(e) => set("law_firm", e.target.value)} placeholder="Your firm's name" maxLength={120} data-testid={`${prefix}-law-firm-input`} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}-pqe-min`}>Experience (PQE) required</Label>
        <PqeRange form={form} set={set} prefix={prefix} />
      </div>
    </div>
  );
}

export function LegalFields({ form, setForm, prefix = "job" }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-tour="pt-legal-fields" data-testid={`${prefix}-legal-fields`}>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}-practice-area`}>Practice area</Label>
        <PracticeArea id={`${prefix}-practice-area`} value={form.practice_area} onChange={(v) => set("practice_area", v)} testId={`${prefix}-practice-area-input`} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}-pqe-min`}>Experience (PQE) required</Label>
        <PqeRange form={form} set={set} prefix={prefix} />
      </div>
      <BarRequired form={form} set={set} prefix={prefix} />
    </div>
  );
}

export const BarRequired = ({ form, set, prefix }) => (
  <label className="flex items-center gap-2 self-end rounded-[6px] border border-line bg-subtle px-3 py-2.5 text-sm">
    <Checkbox checked={!!form.bar_required} onCheckedChange={(v) => set("bar_required", !!v)} data-testid={`${prefix}-bar-required-checkbox`} />
    Bar enrolment required
  </label>
);
